import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Inbox, Package, School as SchoolIcon, Store, ShoppingBag, Wallet, Search, Download, Mail, Phone, MessageSquare, RefreshCw, Settings as SettingsIcon } from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

function fmt(dt) {
  try {
    const d = new Date(dt);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return dt || "—"; }
}

export default function Enquiries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact");
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      [r.name, r.email, r.school, r.mobile, r.message].some(
        (v) => (v || "").toLowerCase().includes(needle),
      ),
    );
  }, [q, rows]);

  return (
    <DashboardLayout nav={NAV} title="Contact Enquiries">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="text-2xl font-heading font-semibold text-brand-navy" data-testid="enq-title">
            Contact enquiries
          </div>
          <div className="text-sm text-slate-500">
            {rows.length} total · showing {filtered.length}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="enq-search"
              placeholder="Search name, email, school…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 pr-4 py-2 w-72 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
            />
          </div>
          <button
            onClick={load}
            data-testid="enq-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-sm hover:border-slate-400"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <a
            href={`${API_BASE}/admin/contact/export.csv`}
            data-testid="enq-export"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark"
          >
            <Download size={14} /> Export CSV
          </a>
        </div>
      </div>

      <div className="sl-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Received</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">School</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Message preview</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-500" data-testid="enq-empty">
                No enquiries {q ? "match your search" : "yet"}.
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`enq-row-${r.id}`}>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmt(r.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-slate-800">{r.name}</td>
                <td className="px-4 py-3 text-slate-600">{r.school || "—"}</td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="flex flex-col">
                    <a href={`mailto:${r.email}`} className="hover:text-brand-blue">{r.email}</a>
                    {r.mobile && <span className="text-xs text-slate-500">{r.mobile}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 max-w-md truncate" title={r.message}>
                  {r.message}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelected(r)}
                    data-testid={`enq-view-${r.id}`}
                    className="text-brand-blue font-semibold hover:text-brand-blueDark"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="enq-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
                  Enquiry
                </div>
                <h3 className="mt-1 text-lg font-heading font-semibold text-brand-navy">{selected.name}</h3>
                <div className="text-xs text-slate-500 mt-0.5">{fmt(selected.created_at)}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="enq-drawer-close"
                className="text-slate-400 hover:text-slate-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <Mail size={16} className="text-brand-blue mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Email</div>
                  <a href={`mailto:${selected.email}`} className="font-semibold text-brand-navy hover:text-brand-blue">
                    {selected.email}
                  </a>
                </div>
              </div>
              {selected.mobile && (
                <div className="flex items-start gap-3">
                  <Phone size={16} className="text-brand-blue mt-0.5" />
                  <div>
                    <div className="text-slate-500 text-xs">Mobile</div>
                    <a href={`tel:${selected.mobile}`} className="font-semibold text-brand-navy hover:text-brand-blue">
                      {selected.mobile}
                    </a>
                  </div>
                </div>
              )}
              {selected.school && (
                <div className="flex items-start gap-3">
                  <SchoolIcon size={16} className="text-brand-blue mt-0.5" />
                  <div>
                    <div className="text-slate-500 text-xs">School</div>
                    <div className="font-semibold text-brand-navy">{selected.school}</div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <MessageSquare size={16} className="text-brand-blue mt-0.5" />
                <div className="flex-1">
                  <div className="text-slate-500 text-xs mb-1">Message</div>
                  <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                    {selected.message}
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-100">
                <a
                  href={`mailto:${selected.email}?subject=SchoolLane%20follow-up&body=Hi%20${encodeURIComponent(selected.name)}%2C%0A%0AThanks%20for%20reaching%20out%20to%20SchoolLane.`}
                  data-testid="enq-reply"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white font-semibold text-sm hover:bg-brand-blueDark"
                >
                  <Mail size={14} /> Reply via email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
